
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {walkPolylineScreen} from "./polylineGeometry.js";

// Geometric core for the Midterlinje/Connector tool: finds the connector
// segment between two digitized helper polylines (Vector 1, Vector 2) that
// is the best *simultaneous* compromise perpendicular to both of them - not
// a connector that is exactly perpendicular to one and merely touches the
// other, which is what the previous implementation (polylineGeometry.js's
// now-removed findConnectorScreen) actually produced: it always picked one
// polyline as a cursor-driven "driver" and set the other endpoint via
// closestPointOnPolyline(), which is mathematically guaranteed perpendicular
// to the *follower* only - the driver side had no angular constraint at all.
//
//   Midterlinje connector geometry
//           |
//           +-- computeBalancedPerpendicularDirection() - the optimal
//           |   connector DIRECTION for a segment pair, independent of
//           |   position (closed form, symmetric in the two directions)
//           |
//           +-- computeConnectorForSegmentPair() - given that direction,
//           |   the closed-form best contact points P1/P2 on the two
//           |   segments (also closed form, no iteration)
//           |
//           +-- computeBestConnector() - evaluates every segment pair of
//               two polylines and keeps the lowest-error one
//
// ── The math ──────────────────────────────────────────────────────────────
//
// All angle/direction reasoning happens in the XY plane (matching the rest
// of the "Perpendicular / Offset geometry" family - see
// geometry/perpendicularConstraint.js's own flattenXY convention); Z is
// tracked separately and linearly interpolated for the actual 3D contact
// points, never flattened away.
//
// For a single segment pair with (unit, undirected-line) local directions
// d̂1, d̂2, the true goal (task's own formulation) is a connector direction Ĉ
// minimizing
//
//   E(Ĉ) = (Ĉ·d̂1)² + (Ĉ·d̂2)²
//
// over unit vectors Ĉ. Writing this as a quadratic form, E(Ĉ) = ĈᵀMĈ where
// M = d̂1d̂1ᵀ + d̂2d̂2ᵀ is a symmetric 2×2 matrix - so the minimizing Ĉ is the
// eigenvector of M's *smallest* eigenvalue (Rayleigh quotient). For 2×2 M
// this has a short closed form in terms of the doubled angles of d̂1, d̂2:
// with d̂1 = (cos α, sin α), d̂2 = (cos β, sin β),
//
//   M = I + ½·R,  R = [[cos2α+cos2β, sin2α+sin2β], [sin2α+sin2β, -(cos2α+cos2β)]]
//
// R is |S| times a reflection matrix (S = (cos2α+cos2β, sin2α+sin2β)), so its
// eigenvalues are ±|S| with eigenvectors at angle ½∠S and ½∠S+90°. M's
// *smaller* eigenvalue is therefore 1-|S|/2, at angle ½∠S+90° - i.e. the
// optimal connector direction is
//
//   θ* = ½·atan2(sin2α+sin2β, cos2α+cos2β) + 90°
//
// Doubling the angles before averaging is what correctly handles the ±180°
// ambiguity of an *undirected* line (d̂ and -d̂ describe the same helper
// vector's line): doubling maps both to the same value, so which way each
// helper vector happens to have been digitized never matters. The whole
// expression is symmetric in (α, β) - swapping the two vectors leaves θ*
// unchanged - which is exactly the symmetry the task requires (section 8):
// neither vector is privileged.
//
// Sanity checks (see midlineConnector.test.js for the actual assertions):
//  - d̂1 = d̂2 (parallel lines): θ* = α+90°, the classical shortest-
//    perpendicular-to-both direction.
//  - d̂1⊥d̂2 exactly (|S|≈0): M = I, isotropic - every direction gives the
//    same error 1, so no direction is privileged; handled as an explicit
//    fallback (the bisector of d̂1,d̂2, rotated 90°) rather than dividing by
//    a near-zero magnitude.
//  - d1=0°, d2=10°: θ* = 95° (absolute) - 5° off perpendicular to each,
//    split evenly, matching the task's own worked example.
//
// θ* depends only on d̂1, d̂2 - not on where along either segment the
// contact points end up. That leaves the *position* of the connector
// (t on segment 1, s on segment 2) underdetermined by the angle objective
// alone: for two non-parallel infinite lines, connectors of the exact
// direction θ* form a whole one-parameter family (a line in (t,s)-space),
// and minimizing raw connector length within that family degenerates to the
// lines' own intersection point (a zero-length "connector", not useful).
// Instead computeConnectorForSegmentPair() picks the point in that family
// closest, in parameter space, to a supplied initial guess (t0, s0) - the
// cursor's current screen-driven position for the interactive tool (see
// midlineTool.js), or the classical closest-points-between-segments point
// when no such guess is available (the pure/tested path, and the initial
// per-segment-pair estimate used to rank segment pairs against each other).
// That is a linearly-constrained least-squares problem with a closed-form
// (Lagrange multiplier) solution - see refineParams() - so the whole
// pipeline is analytical end to end, no iteration.

function clamp01(x){
	return Math.max(0, Math.min(1, x));
}

function normalize2(x, y){
	const len = Math.hypot(x, y);
	return len < 1e-12 ? null : {x: x / len, y: y / len};
}

// The closed-form optimal connector DIRECTION for two (unit, XY, undirected-
// line) local directions - see file doc comment. Returns a unit {x,y}, or
// null only if either input is a zero vector (degenerate/zero-length
// segment - callers skip that segment pair entirely).
export function computeBalancedPerpendicularDirection(d1, d2){
	const a = normalize2(d1.x, d1.y);
	const b = normalize2(d2.x, d2.y);
	if(!a || !b) return null;

	// cos(2α) = a.x²-a.y², sin(2α) = 2·a.x·a.y (double-angle identities from
	// unit components directly - no atan2/trig round-trip needed here)
	const sx = (a.x * a.x - a.y * a.y) + (b.x * b.x - b.y * b.y);
	const sy = 2 * a.x * a.y + 2 * b.x * b.y;
	const sLen = Math.hypot(sx, sy);

	if(sLen < 1e-9){
		// d1 and d2 are (numerically) exactly perpendicular as undirected
		// lines - M is isotropic, every direction ties for optimal. Fall
		// back to the angle bisector of d1,d2 (flipping b if needed so the
		// bisector isn't itself degenerate), rotated 90° - an arbitrary but
		// deterministic, still-symmetric choice for this measure-zero case.
		let bis = normalize2(a.x + b.x, a.y + b.y);
		if(!bis) bis = normalize2(a.x - b.x, a.y - b.y);
		if(!bis) bis = {x: 1, y: 0};
		return {x: -bis.y, y: bis.x};
	}

	const halfAngle = Math.atan2(sy, sx) / 2;
	const perpAngle = halfAngle + Math.PI / 2;
	return {x: Math.cos(perpAngle), y: Math.sin(perpAngle)};
}

// Closest points between two 2D segments (a0,a1) and (b0,b1), clamped to
// [0,1] on each - the classical segment-segment closest-point algorithm
// (Ericson, "Real-Time Collision Detection" §5.1.9). Used only as the
// initial-guess source when no cursor/screen hint is available (the pure/
// tested entry point) and to rank segment pairs against each other.
function closestParamsBetweenSegmentsXY(a0, a1, b0, b1){
	const d1x = a1.x - a0.x, d1y = a1.y - a0.y;
	const d2x = b1.x - b0.x, d2y = b1.y - b0.y;
	const rx = a0.x - b0.x, ry = a0.y - b0.y;

	const aa = d1x * d1x + d1y * d1y;
	const ee = d2x * d2x + d2y * d2y;
	const ff = d2x * rx + d2y * ry;

	const EPS = 1e-12;
	let t, s;

	if(aa <= EPS && ee <= EPS){
		return {t: 0, s: 0};
	}else if(aa <= EPS){
		t = 0;
		s = clamp01(ff / ee);
	}else{
		const cc = d1x * rx + d1y * ry;
		if(ee <= EPS){
			s = 0;
			t = clamp01(-cc / aa);
		}else{
			const bb = d1x * d2x + d1y * d2y;
			const denom = aa * ee - bb * bb;
			t = denom > EPS ? clamp01((bb * ff - cc * ee) / denom) : 0;
			s = (bb * t + ff) / ee;
			if(s < 0){
				s = 0;
				t = clamp01(-cc / aa);
			}else if(s > 1){
				s = 1;
				t = clamp01((bb - cc) / aa);
			}
		}
	}

	return {t, s};
}

// Constrained least-squares refinement (closed form - see file doc comment):
// among the (t,s) pairs whose realized connector P2(s)-P1(t) is exactly
// parallel to `direction`, returns the one closest to (t0,s0) in parameter
// space. `ctx` is {a0x,a0y,dAx,dAy,b0x,b0y,dBx,dBy} (raw XY components -
// segment starts and, deliberately, *unnormalized* direction vectors, since
// t/s are the segments' own [0,1] parameters).
function refineParams(t0, s0, ctx, direction){
	const {a0x, a0y, dAx, dAy, b0x, b0y, dBx, dBy} = ctx;

	// N: perpendicular to the target direction. The constraint "P2-P1
	// parallel to `direction`" is equivalent to "P1 and P2 have the same
	// projection onto N".
	const nx = -direction.y, ny = direction.x;

	const nA = dAx * nx + dAy * ny;
	const nB = dBx * nx + dBy * ny;
	const denom = nA * nA + nB * nB;

	if(denom < 1e-12){
		// both segments run (numerically) parallel to the target direction -
		// sliding along either one can't change the realized direction at
		// all, so there is nothing to project onto; keep the initial guess
		return {t: t0, s: s0};
	}

	const dx = (b0x - a0x) * nx + (b0y - a0y) * ny;
	// e0: how far the initial guess already is from satisfying the
	// constraint - the Lagrange-multiplier solution moves (t0,s0) exactly
	// enough along the constraint's own gradient direction to zero this out
	const e0 = t0 * nA - s0 * nB - dx;

	return {
		t: t0 - e0 * nA / denom,
		s: s0 + e0 * nB / denom,
	};
}

// Solves the same linear constraint as refineParams() for one parameter
// given the other held fixed - used only for the clamp/re-optimize step
// (section 11: when the unconstrained solution falls outside a segment,
// clamp that side and re-solve the other against the constraint, rather
// than leaving it at its own unconstrained - now meaningless - value).
// Returns null if the corresponding coefficient is ~0 (that side's segment
// runs parallel to the target direction, so it can't compensate at all).
function solveSGivenT(t, ctx, direction){
	const {a0x, a0y, dAx, dAy, b0x, b0y, dBx, dBy} = ctx;
	const nx = -direction.y, ny = direction.x;
	const nA = dAx * nx + dAy * ny;
	const nB = dBx * nx + dBy * ny;
	if(Math.abs(nB) < 1e-9) return null;
	const dx = (b0x - a0x) * nx + (b0y - a0y) * ny;
	return (t * nA - dx) / nB;
}

function solveTGivenS(s, ctx, direction){
	const {a0x, a0y, dAx, dAy, b0x, b0y, dBx, dBy} = ctx;
	const nx = -direction.y, ny = direction.x;
	const nA = dAx * nx + dAy * ny;
	const nB = dBx * nx + dBy * ny;
	if(Math.abs(nA) < 1e-9) return null;
	const dx = (b0x - a0x) * nx + (b0y - a0y) * ny;
	return (s * nB + dx) / nA;
}

function clampAndReoptimize(t, s, ctx, direction){
	const tOut = t < 0 || t > 1;
	const sOut = s < 0 || s > 1;
	let tc = clamp01(t);
	let sc = clamp01(s);

	if(tOut && !sOut){
		const resolved = solveSGivenT(tc, ctx, direction);
		if(resolved !== null) sc = clamp01(resolved);
	}else if(sOut && !tOut){
		const resolved = solveTGivenS(sc, ctx, direction);
		if(resolved !== null) tc = clamp01(resolved);
	}
	// both out of range: both sides are already at their own nearest valid
	// value: there is no freedom left to re-optimize either against the
	// other without leaving [0,1] again

	return {t: tc, s: sc};
}

// Angle (deviation-from-90°) diagnostics for a realized connector - section
// 14: not currently surfaced in the UI, but exposed so tests/debugging can
// verify a candidate solution is actually better than the old one.
function evaluateConnector(P1, P2, d1u, d2u){
	const cx = P2.x - P1.x, cy = P2.y - P1.y;
	const len = Math.hypot(cx, cy);
	if(len < 1e-9){
		// P1==P2 (the two segments happen to cross exactly at the contact
		// point) - a zero-length "connector" has no direction and is
		// useless as a preview/result, not a free perfect score: treat it
		// as maximally bad (Infinity) so computeBestConnector() only ever
		// returns this when literally every candidate segment pair is
		// degenerate, rather than have float noise in a genuinely-good
		// pair's near-zero error lose to this by epsilon (this is exactly
		// what motivated the fix - see midlineConnector.test.js's "E"
		// group).
		return {error: Infinity, angleToA: null, angleToB: null, deviationA: null, deviationB: null};
	}

	const cxu = cx / len, cyu = cy / len;
	const dotA = cxu * d1u.x + cyu * d1u.y;
	const dotB = cxu * d2u.x + cyu * d2u.y;

	// angle between the connector and each *line* (undirected, hence the
	// abs) - 90° means exactly perpendicular
	const angleToA = (Math.acos(Math.min(1, Math.abs(dotA))) * 180) / Math.PI;
	const angleToB = (Math.acos(Math.min(1, Math.abs(dotB))) * 180) / Math.PI;

	return {
		error: dotA * dotA + dotB * dotB,
		angleToA,
		angleToB,
		deviationA: Math.abs(90 - angleToA),
		deviationB: Math.abs(90 - angleToB),
	};
}

// Full solve for one segment pair. a0/a1/b0/b1 are THREE.Vector3 (or
// {x,y,z}) segment endpoints. `hint`, if given, is {t,s} - an initial guess
// in each segment's own [0,1] parameter (e.g. from the interactive tool's
// screen-cursor position); omitted for the pure/tested path, which falls
// back to the classical closest-points-between-segments guess instead.
// Returns null for a degenerate (near-zero-length) segment, otherwise
// {P1, P2, t, s, direction, ...evaluateConnector() diagnostics} with P1/P2
// as THREE.Vector3 (Z linearly interpolated along each segment,
// independently of the XY-only angle math - never flattened away).
export function computeConnectorForSegmentPair(a0, a1, b0, b1, hint = null){
	const dAx = a1.x - a0.x, dAy = a1.y - a0.y;
	const dBx = b1.x - b0.x, dBy = b1.y - b0.y;
	const lenA = Math.hypot(dAx, dAy);
	const lenB = Math.hypot(dBx, dBy);
	if(lenA < 1e-9 || lenB < 1e-9) return null;

	const d1u = {x: dAx / lenA, y: dAy / lenA};
	const d2u = {x: dBx / lenB, y: dBy / lenB};

	const direction = computeBalancedPerpendicularDirection(d1u, d2u);
	if(!direction) return null;

	const initial = hint || closestParamsBetweenSegmentsXY(a0, a1, b0, b1);

	const ctx = {a0x: a0.x, a0y: a0.y, dAx, dAy, b0x: b0.x, b0y: b0.y, dBx, dBy};
	let {t, s} = refineParams(clamp01(initial.t), clamp01(initial.s), ctx, direction);
	({t, s} = clampAndReoptimize(t, s, ctx, direction));

	const P1 = new THREE.Vector3(a0.x + t * dAx, a0.y + t * dAy, a0.z + t * (a1.z - a0.z));
	const P2 = new THREE.Vector3(b0.x + s * dBx, b0.y + s * dBy, b0.z + s * (b1.z - b0.z));

	const diagnostics = evaluateConnector(P1, P2, d1u, d2u);

	return {P1, P2, t, s, direction, d1: d1u, d2: d2u, ...diagnostics};
}

// Screen/cursor-driven entry point for the interactive tool (see
// midlineTool.js) - the direct replacement for the old findConnectorScreen()
// (same call shape: already-projected screen points + walker state per
// polyline for continuous, hysteresis-based segment tracking - see
// walkPolylineScreen()'s own doc comment; that mechanism is unchanged, only
// what happens *after* picking a segment on each side is new). Still pure/
// camera-DOM-free (screen points are passed in already projected), so it's
// unit-testable the same way findConnectorScreen's tests were.
//
// Unlike the old function, this never designates one polyline a "driver"
// whose raw cursor-nearest point is used as-is: both walkPolylineScreen
// results are only an *initial guess* (t on segment A, s on segment B),
// which computeConnectorForSegmentPair() then refines (closed form) toward
// the jointly-optimal direction - so the preview always shows the same
// solution finalizeMidline() would commit (section 13), not a cheaper
// approximation.
export function computeConnectorScreen(pointsA, pointsB, screenA, screenB, mouse, walkerA = {}, walkerB = {}){
	if(!pointsA || !pointsB || pointsA.length < 2 || pointsB.length < 2) return null;

	const nearA = walkPolylineScreen(mouse, pointsA, screenA, walkerA);
	const nearB = walkPolylineScreen(mouse, pointsB, screenB, walkerB);
	if(!nearA || !nearB) return null;

	const result = computeConnectorForSegmentPair(
		pointsA[nearA.segmentIndex], pointsA[nearA.segmentIndex + 1],
		pointsB[nearB.segmentIndex], pointsB[nearB.segmentIndex + 1],
		{t: nearA.t, s: nearB.t});
	if(!result) return null;

	return {...result, segmentIndexA: nearA.segmentIndex, segmentIndexB: nearB.segmentIndex};
}

// Evaluates every segment pair of two polylines (>=2 points each) and keeps
// the one with the lowest realized angular error (section 6: "vælges den
// bedste samlede løsning blandt relevante segmentpar") - the pure, camera/
// DOM-free entry point used directly by unit tests and by the interactive
// tool's own per-frame update when it needs a fresh global search (see
// midlineTool.js). Returns null if either polyline has fewer than 2 points,
// or no segment pair is non-degenerate.
export function computeBestConnector(pointsA, pointsB){
	if(!pointsA || !pointsB || pointsA.length < 2 || pointsB.length < 2) return null;

	let best = null;
	for(let i = 0; i < pointsA.length - 1; i++){
		for(let j = 0; j < pointsB.length - 1; j++){
			const candidate = computeConnectorForSegmentPair(pointsA[i], pointsA[i + 1], pointsB[j], pointsB[j + 1]);
			if(!candidate) continue;
			if(!best || candidate.error < best.error){
				best = {...candidate, segmentIndexA: i, segmentIndexB: j};
			}
		}
	}
	return best;
}
